/**
 * Shared helpers for detecting and extracting handoff translation metadata.
 *
 * The `metadata` field on messages can arrive from the backend in two shapes:
 *   1. An already-parsed object (in-memory SSE payloads, history API)
 *   2. A JSON string (Neo4j stores metadata as stringified JSON; some SSE paths
 *      pass it through un-parsed)
 *
 * Both shapes are handled here so callers don't need to know the encoding.
 */

/** Minimal shape required to conclude a message has translation metadata. */
interface TranslationMeta {
  original_language: string;
  translations?: Partial<Record<'en' | 'es' | 'zh' | 'fil' | 'pt' | 'fr', string>>;
  translation_error?: string | null;
}

/**
 * Returns true when the given metadata field (object or JSON string) contains
 * `original_language` — the discriminating key for translation metadata.
 */
export function hasTranslationMetadata(metadata: unknown): boolean {
  if (!metadata) return false;
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return typeof parsed === 'object' && parsed !== null && 'original_language' in parsed;
    } catch {
      return false;
    }
  }
  if (typeof metadata !== 'object') return false;
  return 'original_language' in metadata;
}

/**
 * Parses the metadata field into a TranslationMeta object, or returns null
 * if no translation metadata is present.
 */
export function parseTranslationMeta(metadata: unknown): TranslationMeta | null {
  if (!metadata) return null;
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      if (
        parsed &&
        typeof parsed === 'object' &&
        'original_language' in parsed
      ) {
        return parsed as TranslationMeta;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof metadata !== 'object') return null;
  if ('original_language' in metadata) {
    return metadata as TranslationMeta;
  }
  return null;
}
